import type { DurationLike } from 'luxon';
import { DateTime, Duration } from 'luxon';

import { expectNever } from '../../helpers/expectDefinedOrThrow';

export enum FilterKey {
  StartDate = 'startDate',
  EndDate = 'endDate',
  Value = 'value',
  Interval = 'interval',
}

export enum IntervalUnits {
  Milliseconds = 'milliseconds',
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
}
// Keep this in sync with IntervalUnits, it should be possible to compute this but it's hard
export type IntervalUnitsProp = 'milliseconds' | 'seconds' | 'minutes' | 'hours';

export type SettableFilterKey = Exclude<FilterKey, FilterKey.Interval>;

export type Updater<T> = T | ((prev: T) => T);

const min = 60;
const hour = min * 60;
const day = hour * 24;
const month = 30 * day;

export const intervalUnitsToSeconds: Record<IntervalUnits, number> = {
  [IntervalUnits.Milliseconds]: 1 / 1000,
  [IntervalUnits.Seconds]: 1,
  [IntervalUnits.Minutes]: min,
  [IntervalUnits.Hours]: hour,
};

export enum TimePeriod {
  FiveMinutes = 5 * min,
  TenMinutes = 10 * min,
  ThirtyMinutes = 30 * min,
  OneHour = hour,
  TwoHours = 2 * hour,
  ThreeHours = 3 * hour,
  SixHours = 6 * hour,
  TwelveHours = 12 * hour,
  OneDay = day,
  SevenDays = 7 * day,
  ThirtyDays = 30 * day,
  ThreeMonths = 3 * month,
  SixMonths = 6 * month,
  NineMonths = 9 * month,
  OneYear = 12 * month,
  Custom = 'CUSTOM',
}

export function timePeriodLabel(period: TimePeriod): string {
  switch (period) {
    case TimePeriod.FiveMinutes:
      return '5 minutes';
    case TimePeriod.TenMinutes:
      return '10 minutes';
    case TimePeriod.ThirtyMinutes:
      return '30 minutes';
    case TimePeriod.OneHour:
      return '1 hour';
    case TimePeriod.TwoHours:
      return '2 hours';
    case TimePeriod.ThreeHours:
      return '3 hours';
    case TimePeriod.SixHours:
      return '6 hours';
    case TimePeriod.TwelveHours:
      return '12 hours';
    case TimePeriod.OneDay:
      return '24 hours';
    case TimePeriod.SevenDays:
      return '7 days';
    case TimePeriod.ThirtyDays:
      return '30 days';
    case TimePeriod.ThreeMonths:
      return '3 months';
    case TimePeriod.SixMonths:
      return '6 months';
    case TimePeriod.NineMonths:
      return '9 months';
    case TimePeriod.OneYear:
      return '1 year';
    case TimePeriod.Custom:
      return 'Custom';
    default:
      return 'Time period';
  }
}

// eslint-disable-next-line consistent-return
export function timePeriodLabelFromString(period: string): string {
  switch (period) {
    case '5':
      return 'Last 5 minutes';
    case '10':
      return 'Last 10 minutes';
    case '30':
      return 'Last 30 minutes';
    case '60':
      return 'Last hour';
    case '120':
      return 'Last 2 hours';
    case '180':
      return 'Last 3 hours';
    case '360':
      return 'Last 6 hours';
    case '720':
      return 'Last 12 hours';
    case '1440':
      return 'Last 24 hours';
    case '10080':
      return 'Last 7 days';
    case '43200':
      return 'Last 30 days';
    default:
      return '';
  }
}

export function getStep(currentTimePeriod: number, defaultStepSeconds: number = 120): number {
  if (currentTimePeriod >= TimePeriod.ThreeMonths) return TimePeriod.OneDay;
  if (currentTimePeriod >= TimePeriod.ThirtyDays) return TimePeriod.ThreeHours;
  if (currentTimePeriod >= TimePeriod.SevenDays) return TimePeriod.OneHour;
  if (currentTimePeriod >= TimePeriod.OneDay) return TimePeriod.TenMinutes;
  if (currentTimePeriod >= TimePeriod.SixHours) return 2 * min;

  return defaultStepSeconds;
}

export const maxDuration = 6000;

export function getIntervalFromNow(duration: DurationLike): number {
  return DateTime.now().startOf('day').minus(duration).toSeconds();
}

export function getIntervalDuration(
  currentTimePeriod: TimePeriod,
  units: IntervalUnits = IntervalUnits.Seconds,
): number {
  if (currentTimePeriod === TimePeriod.Custom) return 0;

  switch (units) {
    case IntervalUnits.Milliseconds:
      return currentTimePeriod * 1000;
    case IntervalUnits.Seconds:
      return currentTimePeriod;
    case IntervalUnits.Minutes:
      return currentTimePeriod / min;
    case IntervalUnits.Hours:
      return currentTimePeriod / hour;
    default:
      expectNever(units);
      return currentTimePeriod;
  }
}

export function formatToISO(dateTime: DateTime) {
  return dateTime.toISO({
    includeOffset: true,
    suppressSeconds: false,
    suppressMilliseconds: true,
  });
}

const RANGE_LENGTH_UNITS = ['years', 'months', 'weeks', 'days', 'hours', 'minutes'] as const;

/** The two largest whole units between `start` and `end`, e.g. "1 yr, 2 mths" or "3 hr, 30 min". */
export function formatDateTimeRangeLength(start: DateTime, end: DateTime): string | null {
  if (!start.isValid || !end.isValid || end <= start) return null;

  const diff = end.diff(start, [...RANGE_LENGTH_UNITS]);
  const parts = RANGE_LENGTH_UNITS.map((unit) => [unit, Math.floor(diff.get(unit))] as const)
    .filter(([, amount]) => amount > 0)
    .slice(0, 2);

  if (parts.length === 0) return 'Under a minute';
  return Duration.fromObject(Object.fromEntries(parts)).toHuman({ unitDisplay: 'short' });
}

/** Hours of each day a range keeps, as `HH:mm` wall-clock times. An end before the start wraps past midnight. */
export interface DailyTimeWindow {
  start: string;
  end: string;
}

const DAILY_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

function minutesOfDay(time: string): number | null {
  const match = DAILY_TIME_PATTERN.exec(time);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

export function isValidDailyTimeWindow(window: DailyTimeWindow): boolean {
  const start = minutesOfDay(window.start);
  const end = minutesOfDay(window.end);
  return start != null && end != null && start !== end;
}

/** Whether `dateTime`'s wall-clock time, in its own zone, falls in `[start, end)`. */
export function isWithinDailyTimeWindow(dateTime: DateTime, window: DailyTimeWindow): boolean {
  const start = minutesOfDay(window.start);
  const end = minutesOfDay(window.end);
  if (start == null || end == null || start === end) return true;

  const minutes = dateTime.hour * 60 + dateTime.minute;
  return start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}

/** Tests instants against the window's hours as they fall in `zone`, the local zone by default. */
export function getDailyTimeWindowFilter(
  window: DailyTimeWindow | null | undefined,
  zone?: string,
): ((date: Date) => boolean) | null {
  if (!window || !isValidDailyTimeWindow(window)) return null;
  return (date) => isWithinDailyTimeWindow(DateTime.fromJSDate(date, { zone }), window);
}

export interface TimeInterval {
  start: number;
  end: number;
}

/** The parts of `[start, end]`, in epoch milliseconds, that fall within the window's hours in `zone`. */
export function getDailyTimeWindowIntervals(
  window: DailyTimeWindow,
  start: number,
  end: number,
  zone?: string,
): TimeInterval[] {
  const startMinutes = minutesOfDay(window.start);
  const endMinutes = minutesOfDay(window.end);
  if (startMinutes == null || endMinutes == null || startMinutes === endMinutes || end <= start) {
    return [];
  }

  const intervals: TimeInterval[] = [];
  const lastDay = DateTime.fromMillis(end, { zone }).startOf('day');
  // From the day before, so a window that wraps past midnight still covers the range's start.
  let date = DateTime.fromMillis(start, { zone }).startOf('day').minus({ days: 1 });
  while (date <= lastDay) {
    const windowStart = date.plus({ minutes: startMinutes });
    const windowEnd = date.plus({ days: endMinutes < startMinutes ? 1 : 0, minutes: endMinutes });
    const clippedStart = Math.max(windowStart.toMillis(), start);
    const clippedEnd = Math.min(windowEnd.toMillis(), end);
    if (clippedEnd > clippedStart) intervals.push({ start: clippedStart, end: clippedEnd });
    date = date.plus({ days: 1 });
  }
  return intervals;
}
